import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { TrendUp, HandCoins, Warning, PaperPlaneRight, Sparkle, Plus, ChartDonut, Vault, ArrowRight, DownloadSimple } from "@phosphor-icons/react";
import api, { fmtGNF, fmtDate, readSse, logError, apiError } from "../lib/api";
import { SeverityIcon } from "../lib/display";
import { StatusBadge } from "../components/StatusBadge";
import { useAuth } from "../context/AuthContext";

const PERIODS = [["7d", "7 jours"], ["30d", "30 jours"], ["month", "Mois"], ["custom", "Personnalisé"]];
const DONUT_COLORS = ["#16233D", "#C9793A", "#3B6AA0", "#7A8CA8", "#C99A2E", "#8C6B4F", "#5B7C6E"];

/** Accueil simplifié : employé sans le droit « voir le dashboard ». */
function QuickHome() {
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const actions = [
    can("sales_create") && ["/ventes/nouvelle", "Nouvelle vente", "Encaisser un client", Plus],
    can("sales_create") && ["/ventes", "Mes ventes", "Historique et encaissements", TrendUp],
    can("products_view") && ["/stock", "Stock", "Produits et quantités disponibles", ChartDonut],
    can("clients_view") && ["/clients", "Clients", "Fiches et contacts", HandCoins],
  ].filter(Boolean);
  return (
    <div className="space-y-5 fade-in" data-testid="dashboard-page">
      <div>
        <h1 className="font-display font-bold text-2xl">Bonjour {user?.name?.split(" ")[0] || ""}</h1>
        <p className="text-sm text-gray-500">Voici vos raccourcis du jour.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {actions.map(([to, label, desc, Icon]) => (
          <button key={to} data-testid={`quick-${to.replaceAll("/", "-")}`} onClick={() => navigate(to)}
            className="kcard p-5 text-left flex items-start gap-3 hover:shadow-md transition-shadow">
            <Icon size={20} weight="bold" className="text-[#C9793A] shrink-0 mt-0.5" />
            <span>
              <span className="font-display font-bold text-sm block">{label}</span>
              <span className="text-xs text-gray-500">{desc}</span>
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

function AiBlock() {
  const [summary, setSummary] = useState("");
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [asking, setAsking] = useState(false);

  useEffect(() => {
    api.get("/ai/summary").then((r) => setSummary(r.data.summary)).catch((e) => {
      logError("Résumé IA indisponible", e);
      setSummary("");
    });
  }, []);

  const ask = async (e) => {
    e.preventDefault();
    if (!question.trim() || asking) return;
    setAsking(true); setAnswer("");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/ask`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question }),
      });
      await readSse(res, (j) => {
        if (j.delta) setAnswer((a) => a + j.delta);
        if (j.error) setAnswer("L'assistant est indisponible pour le moment.");
      });
    } catch (err) {
      logError("Assistant indisponible", err);
      setAnswer("L'assistant est indisponible pour le moment.");
    } finally { setAsking(false); }
  };

  return (
    <div>
      {summary && (
        <p data-testid="ai-summary" className="text-sm bg-[#16233D] text-white/90 rounded-2xl px-4 py-3 mb-3 flex gap-2">
          <Sparkle size={18} weight="fill" color="#C9793A" className="shrink-0 mt-0.5" />
          <span>{summary}</span>
        </p>
      )}
      {answer && <p data-testid="ai-answer" className="text-sm bg-[#F6F4EF] rounded-2xl px-4 py-3 mb-3 whitespace-pre-wrap">{answer}</p>}
      <form onSubmit={ask} className="flex gap-2">
        <input data-testid="ai-question-input" className="kinput flex-1" placeholder="Posez une question : combien j'ai vendu aujourd'hui ?"
          value={question} onChange={(e) => setQuestion(e.target.value)} />
        <button data-testid="ai-ask-btn" className="btn-ghost px-4 flex items-center" disabled={asking}>
          <PaperPlaneRight size={18} color={asking ? "#aaa" : "#C9793A"} weight="fill" />
        </button>
      </form>
    </div>
  );
}

function KpiCard({ testid, label, value, icon, color }) {
  const Icon = icon;
  return (
    <div data-testid={testid} className="kcard p-5">
      <p className="text-xs uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1.5">
        <Icon size={15} weight="fill" color={color} /> {label}
      </p>
      <p className="font-display font-bold text-xl lg:text-2xl tabular-nums">{fmtGNF(value)}</p>
    </div>
  );
}

function DonutCard({ donut }) {
  const totalExp = donut.reduce((a, b) => a + b.amount, 0);
  return (
    <div className="kcard p-5" data-testid="expense-donut-card">
      <h2 className="font-display font-bold text-lg mb-2 flex items-center gap-2"><ChartDonut size={20} /> Répartition des dépenses</h2>
      {donut.length === 0 ? <p className="text-sm text-gray-400 py-8">Aucune dépense sur la période.</p> : (
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="w-40 h-40 shrink-0" style={{ minHeight: 160, minWidth: 160 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={donut} dataKey="amount" nameKey="category" innerRadius={45} outerRadius={70} paddingAngle={2}>
                  {donut.map((d, i) => <Cell key={d.category} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex-1 w-full space-y-1.5">
            {donut.map((d, i) => (
              <div key={d.category} className="flex items-center gap-2 text-sm">
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                <span className="flex-1 truncate">{d.category}</span>
                <span className="text-gray-500">{totalExp ? Math.round((d.amount / totalExp) * 100) : 0}%</span>
                <span className="font-semibold tabular-nums">{fmtGNF(d.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DebtsCard({ combined, title }) {
  return (
    <div className="kcard p-5" data-testid="debts-list-card">
      <h2 className="font-display font-bold text-lg mb-3">{title}</h2>
      {combined.length === 0 ? <p className="text-sm text-gray-400 py-8">Rien en attente. Tout est réglé.</p> : (
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {combined.map((it) => (
            <div key={it.kind + it.id} data-testid={`debt-item-${it.kind}`} className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[#F6F4EF]/60">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold truncate">{it.name} {it.phone ? <span className="font-normal text-gray-500">· {it.phone}</span> : null}</p>
                <p className="text-xs text-gray-500">{it.kind === "receivable" ? "Vous doit" : "Vous devez"} · {fmtDate(it.date)}</p>
              </div>
              <span className="font-semibold text-sm tabular-nums">{fmtGNF(it.remaining)}</span>
              <StatusBadge status={it.status} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [period, setPeriod] = useState("7d");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();
  const { estId, can } = useAuth();
  const canDashboard = can("view_dashboard");

  const periodQuery = useCallback(() => {
    let q = `period=${period}`;
    if (period === "custom" && custom.start && custom.end) q += `&start=${custom.start}&end=${custom.end}`;
    if (estId) q += `&establishment_id=${estId}`;
    return q;
  }, [period, custom, estId]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/dashboard/export?${periodQuery()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "kavizio-export.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { logError("Export Excel", e); } finally { setExporting(false); }
  };

  const load = useCallback(() => {
    if (!canDashboard) return;
    let url = `/dashboard?period=${period}`;
    if (period === "custom") {
      if (!custom.start || !custom.end) return;
      url += `&start=${custom.start}&end=${custom.end}`;
    }
    if (estId) url += `&establishment_id=${estId}`;
    api.get(url)
      .then((r) => { setData(r.data); setLoadError(""); })
      .catch((e) => { logError("Chargement dashboard", e); setLoadError(apiError(e)); });
  }, [period, custom, estId, canDashboard]);
  useEffect(() => { load(); }, [load]);

  // Un employé sans le droit « voir le dashboard » reçoit un accueil simple, jamais un écran vide.
  if (!canDashboard) return <QuickHome />;
  if (loadError) return (
    <div className="kcard p-8 text-center space-y-3" data-testid="dashboard-error">
      <p className="text-sm text-[#B4432F]">{loadError}</p>
      <button className="btn-ghost py-2 px-4 text-sm" onClick={load}>Réessayer</button>
    </div>
  );
  if (!data) return <p className="text-sm text-gray-400 py-10">Chargement...</p>;
  const k = data.kpis;
  const sub = data.subscription || {};
  const perms = data.permissions || { finance: false, clients: false, suppliers: false };
  const combined = [...data.receivables_list, ...data.payables_list];

  return (
    <div className="space-y-5 fade-in" data-testid="dashboard-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1.5 flex-wrap">
          {PERIODS.map(([v, l]) => (
            <button key={v} data-testid={`period-${v}`} onClick={() => setPeriod(v)}
              className={`px-3.5 py-1.5 rounded-full text-sm transition-colors ${period === v ? "bg-[#16233D] text-white font-semibold" : "bg-white border border-[#E9E4DA] text-gray-600 hover:bg-[#F6F4EF]"}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-center">
          {perms.export && (
            <button data-testid="dashboard-export-btn" onClick={exportExcel} disabled={exporting}
              className="btn-ghost py-2.5 px-4 flex items-center gap-1.5 text-sm">
              <DownloadSimple size={16} /> {exporting ? "Export..." : "Exporter en Excel"}
            </button>
          )}
          <button data-testid="new-sale-btn-dashboard" onClick={() => navigate("/ventes/nouvelle")}
            className="btn-amber py-2.5 px-5 hidden md:flex items-center gap-2 text-sm">
            <Plus size={16} weight="bold" /> Nouvelle vente
          </button>
        </div>
      </div>
      {period === "custom" && (
        <div className="flex gap-2">
          <input data-testid="custom-start-input" type="date" className="kinput max-w-[180px]" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })} />
          <input data-testid="custom-end-input" type="date" className="kinput max-w-[180px]" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })} />
        </div>
      )}

      <div data-testid="kpi-profit" className="rounded-[20px] p-6 text-white" style={{ background: "#16233D" }}>
        <p className="text-xs uppercase tracking-wider text-white/60 mb-1">{perms.profit ? "Bénéfice net" : "Chiffre d'affaires"}</p>
        <p className="font-display font-bold kpi-value text-3xl lg:text-4xl tabular-nums">{fmtGNF(perms.profit ? k.profit : k.revenue)}</p>
        <p className="text-xs text-white/50 mt-1">{data.sales_count} vente(s) sur la période</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {perms.profit && <KpiCard testid="kpi-revenue" label="Chiffre d'affaires" value={k.revenue} icon={TrendUp} color="#2E7D5B" />}
        {perms.cash && <KpiCard testid="kpi-treasury" label="Trésorerie disponible" value={k.treasury} icon={Vault} color="#3B6AA0" />}
        {perms.clients && <KpiCard testid="kpi-receivables" label="On vous doit" value={k.receivables} icon={HandCoins} color="#C9793A" />}
        {perms.suppliers && <KpiCard testid="kpi-payables" label="Vous devez" value={k.payables} icon={Warning} color="#B4432F" />}
      </div>

      {sub.dashboard_details && perms.advanced && (
        <button data-testid="dashboard-see-more-btn" onClick={() => navigate("/tableau-detaille")}
          className="w-full kcard p-4 flex items-center justify-between hover:shadow-md transition-shadow">
          <span className="text-left">
            <span className="font-display font-bold text-sm block">Voir plus</span>
            <span className="text-xs text-gray-500">Top produits, top clients, évolution du chiffre d'affaires et conseils IA approfondis</span>
          </span>
          <ArrowRight size={18} className="text-[#C9793A] shrink-0" />
        </button>
      )}

      {sub.ai && (
        <div className="kcard p-5" data-testid="ai-alerts-block">
          <h2 className="font-display font-bold text-lg mb-3">Assistant Kavizio</h2>
          <AiBlock />
        </div>
      )}

      {data.alerts.length > 0 && (
        <div className="kcard p-5" data-testid="alerts-block">
          <h2 className="font-display font-bold text-lg mb-3">Alertes</h2>
          <div className="space-y-1.5">
            {data.alerts.map((a) => (
              <div key={a.id} data-testid="dashboard-alert" className="flex items-start gap-2 px-3 py-2 rounded-xl hover:bg-[#F6F4EF] transition-colors">
                <SeverityIcon severity={a.severity} />
                <button type="button" className="text-sm text-left flex-1" onClick={() => a.link && navigate(a.link)}>{a.message}</button>
                <button type="button" data-testid="alert-seen-btn" className="text-xs font-semibold text-[#3B6AA0] shrink-0"
                  onClick={async () => { await api.post(`/notifications/${a.id}/seen`).catch(() => {}); load(); }}>Vu</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {perms.profit && <DonutCard donut={data.expense_breakdown} />}
        {(perms.clients || perms.suppliers) && (
          <DebtsCard combined={combined} title={perms.suppliers ? "Créances & dettes en cours" : "Créances en cours"} />
        )}
      </div>
    </div>
  );
}
